import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { PortNameFilters, PortNames } from '../models';
import * as portNamesService from '../services/port-names.service';

export const usePortNames = (defaultFilters: Partial<PortNameFilters & { searchParams: { ids: string[] } }> = {}) => {
  const { searchParams, ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<PortNameFilters>>(otherDefaultFilters);

  const cacheData = portNamesService.getAll();

  const updateFilters = useCallback((newFilters: Partial<PortNameFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const { data, isLoading, isError } = useQuery(portNamesService.portNamesQueryOptions(filters));

  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length || !data?.map) {
      return [];
    }
    return searchParams.ids.filter((id) => !data?.map[id] && !cacheData?.map[id]);
  }, [searchParams?.ids, data?.map, cacheData?.map]);

  const {
    data: preloadData,
    isLoading: isPreloadLoading,
    isError: isPreloadError,
  } = useQuery(
    portNamesService.portNamesQueryOptions(
      { search: { id: missingIds.join(',') } },
      { enabled: missingIds.length > 0 },
    ),
  );

  const mergedMap: PortNames['map'] = {
    ...(data?.map ?? {}),
    ...(cacheData?.map ?? {}),
    ...(preloadData?.map ?? {}),
  };

  return {
    data: {
      list: Object.values(mergedMap),
      map: mergedMap,
    },
    isError: isError || isPreloadError,
    isLoading: isLoading || isPreloadLoading,
    updateFilters,
  };
};
